import { Masonry, Typography } from '@forgeglobal/design-framework';
import ValuationDetailsCard from '@tcs/client-portal-common/components/templates/DetailCards/ValuationDetailsCard';
import Spinner from '@tcs/common/components/atoms/Spinner';
import useIsUserRMD from '@tcs/client-portal-common/hooks/useIsUserRMD';
import { Account } from '@tcs/client-portal-api/types/Account';
import { AssetValuationsGreeting } from '@tcs/client-portal-common/components/molecules/AssetValuationsGreeting';
import useMasonryDynamicColumns from '@tcs/common/hooks/useMasonryDynamicColumns';
import { useStyles } from './AssetValuation.style';
import useValuationDetails from '@tcs/client-portal-common/hooks/useValuationDetails';
import useSession from '@tcs/client-portal-common/hooks/useSession';

interface AssetValuationProps {
  accounts: Account[];
}

const AccountAssetValuation = ({ account }: { account: Account }) => {
  const { accountNumber, displayAccountType } = account;
  const classes = useStyles();
  const columnNum = useMasonryDynamicColumns('lg', 2);

  const { loading: loadingValuation, data: valuationData } =
    useValuationDetails(accountNumber);

  // separate assets by those that can be updated in our portal
  // and those that need to be updated by a third party
  let updatableAssets = [];
  if (valuationData) {
    updatableAssets = valuationData.filter((asset) => asset['isUpdateable']);
  }
  return (
    <div key={accountNumber}>
      <Typography
        variant="h5"
        className={
          classes.accountHeader
        }>{`${displayAccountType} ${accountNumber}`}</Typography>
      {loadingValuation ? (
        <Spinner />
      ) : (
        <>
          {updatableAssets.length > 0 && (
            <Typography
              variant="h6"
              className={classes.title}
              color="textPrimary"
              data-test-id="asset-title">
              Assets Requiring Self Valuation (Real Estate, Private LLC, Trusts
              & Extraordinary)
            </Typography>
          )}
          <Masonry numCols={columnNum}>
            {updatableAssets.map((asset) => (
              <div key={accountNumber + asset.id}>
                <ValuationDetailsCard
                  accountNumber={accountNumber}
                  asset={asset}
                />
              </div>
            ))}
          </Masonry>
        </>
      )}
    </div>
  );
};

export const AssetValuation = ({ accounts }: AssetValuationProps) => {
  //NOTE: this will only return the RMD status of the master account.
  //It will potentially be inaccurate in scenarios with multiple investors who have different RMD statuses!
  const {
    claims: { clientPortalIdentity },
  } = useSession();
  const isUserRMD = useIsUserRMD(clientPortalIdentity);

  return (
    <>
      <AssetValuationsGreeting isUserRMD={isUserRMD} />
      {accounts.map((account) => (
        <AccountAssetValuation account={account} key={account.accountNumber} />
      ))}
    </>
  );
};

export default AssetValuation;
